import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Search } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { CircleUnchecked, CircleChecked } from "./CheckIcons";
import UserAvatar from "./UserAvatar";

const AssignUsersDrawer = ({ open, onClose, pipelineId, assignedUserIds = [], ownerId, onUpdated }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);

  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setError("");
    setSelectedIds(new Set(assignedUserIds.map(Number)));
    setFetching(true);
    sh.get("/internal/search/internal_users")
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]))
      .finally(() => setFetching(false));
  }, [open, assignedUserIds]);

  const filtered = useMemo(() => {
    const base = ownerId ? users.filter((u) => u.id !== ownerId) : users;
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(
      (u) =>
        (u.full_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q),
    );
  }, [users, search, ownerId]);

  const groupedByZone = useMemo(() => {
    const zones = { francophone: [], flemish: [] };
    filtered.forEach((u) => {
      if (zones[u.market_zone]) zones[u.market_zone].push(u);
    });
    return zones;
  }, [filtered]);

  const toggleUser = useCallback((userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const toggleZone = useCallback((zone) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const zoneIds = (groupedByZone[zone] || []).map((u) => u.id);
      const allSelected = zoneIds.every((id) => next.has(id));
      zoneIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }, [groupedByZone]);

  const handleSave = useCallback(() => {
    if (saving || !pipelineId) return;
    setSaving(true);
    setError("");

    sh.post(`/internal/profile_pipelines/${pipelineId}/assign_users`, {
      authenticity_token,
      assigned_internal_user_ids: [...selectedIds],
    })
      .then((res) => {
        onUpdated?.(res.data);
        onClose();
      })
      .catch(() => setError("Failed to update access."))
      .finally(() => setSaving(false));
  }, [authenticity_token, onClose, onUpdated, pipelineId, saving, selectedIds]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 448 },
          maxWidth: "100%",
          borderLeft: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid hsl(140 8% 88%)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 16, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
            Manage access
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "hsl(150 10% 46%)", "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}>
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 1.5, py: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                pointerEvents: "none",
                color: "hsl(150 10% 46%)",
              }}
            >
              <Search size={14} strokeWidth={2} />
            </Box>
            <Box
              component="input"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: "100%",
                height: 40,
                borderRadius: "12px",
                border: "1px solid hsl(140 8% 88%)",
                backgroundColor: "hsl(140 10% 95%)",
                pl: 4,
                pr: 1.5,
                py: 1,
                fontSize: 14,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                outline: "none",
                "&::placeholder": { color: "hsl(150 10% 46%)" },
                "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
              }}
            />
          </Box>
        </Box>

        {error && (
          <Typography sx={{ px: 3, pb: 1, fontSize: 13, color: "hsl(0 72% 51%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
            {error}
          </Typography>
        )}

        <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, pb: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {fetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={24} sx={{ color: "hsl(150 38% 17%)" }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", textAlign: "center", py: 6, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
              No users found
            </Typography>
          ) : (
            Object.entries(groupedByZone)
              .filter(([, zoneUsers]) => zoneUsers.length > 0)
              .map(([zone, zoneUsers]) => {
                const zoneIds = zoneUsers.map((u) => u.id);
                const allZoneSelected = zoneIds.every((id) => selectedIds.has(id));
                const someZoneSelected = !allZoneSelected && zoneIds.some((id) => selectedIds.has(id));
                const zoneLabel = zone === "francophone" ? "Francophone" : "Flemish";

                return (
                  <Box key={zone}>
                    {/* Zone header */}
                    <Box
                      onClick={() => toggleZone(zone)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.5,
                        py: 0.75,
                        cursor: "pointer",
                        borderRadius: "8px",
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      <Checkbox
                        checked={allZoneSelected}
                        indeterminate={someZoneSelected}
                        disableRipple
                        icon={<CircleUnchecked />}
                        checkedIcon={<CircleChecked />}
                        indeterminateIcon={
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: "hsl(150 38% 17%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxSizing: "border-box",
                            }}
                          >
                            <Box sx={{ width: 8, height: 2, backgroundColor: "hsl(80 97% 72%)", borderRadius: 1 }} />
                          </Box>
                        }
                        sx={{ p: 0 }}
                        tabIndex={-1}
                      />
                      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {zoneLabel} team
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", ml: "auto" }}>
                        {zoneIds.filter((id) => selectedIds.has(id)).length}/{zoneIds.length}
                      </Typography>
                    </Box>

                    {/* Zone users */}
                    {zoneUsers.map((user) => {
                      const checked = selectedIds.has(user.id);
                      return (
                        <Box
                          key={user.id}
                          onClick={() => toggleUser(user.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            pl: 4.5,
                            pr: 1.5,
                            py: 0.875,
                            borderRadius: "10px",
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                            backgroundColor: checked ? "hsla(150, 38%, 17%, 0.06)" : "transparent",
                            "&:hover": { backgroundColor: checked ? "hsla(150, 38%, 17%, 0.09)" : "rgba(0,0,0,0.02)" },
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            disableRipple
                            icon={<CircleUnchecked />}
                            checkedIcon={<CircleChecked />}
                            sx={{ p: 0, "&:hover .MuiBox-root": { borderColor: "hsl(150 38% 17%)" } }}
                            tabIndex={-1}
                          />
                          <UserAvatar user={user} size={28} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.3 }} noWrap>
                              {user.full_name || user.email}
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", lineHeight: 1.3 }} noWrap>
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })
          )}
        </Box>

        <Box sx={{ px: 3, py: 2, borderTop: "1px solid hsl(140 8% 88%)" }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={saving}
            sx={{
              height: 40,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)" },
              "&.Mui-disabled": {
                opacity: 0.5,
                backgroundColor: "hsl(150 38% 17%)",
                color: "hsl(80 97% 72%)",
                pointerEvents: "none",
              },
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AssignUsersDrawer;
